/**
 * Categories as things rather than as text.
 *
 * Transactions, plans and budgets point at a row here by id (migration 18);
 * the row is what a category *is* — its name, kept in one place, and the tags
 * it hangs on everything filed under it. The stores join the name back onto
 * each transaction and plan as it loads, because that is what every filter,
 * chart and plan in the app speaks.
 *
 * A category's tags are worn by everything filed under it. They are not copied
 * onto the transactions: the link is the category, so tagging one category
 * tags a thousand rows at once, and untagging it takes them back off.
 */
import { insert, query, run } from '../db'
import { nameId, uid } from '../lib/id'
import { state } from './state'
import { loadPlans } from './plans'
import { loadTransactions } from './transactions'
import { dataChanged } from './live'

const fold = (name) => String(name ?? '').trim().toLowerCase()

export async function loadCategories() {
  state.categories = await query('SELECT * FROM categories ORDER BY name COLLATE NOCASE')
  const links = await query('SELECT category_id, tag_id FROM category_tags')
  const byCategory = {}
  for (const link of links) (byCategory[link.category_id] ??= []).push(link.tag_id)
  state.categoryTags = byCategory
  // A category's name and its tags are read by the queries themselves.
  dataChanged()
  return state.categories
}

/** The existing category of that name, whatever its case. */
export const findCategory = (name) =>
  state.categories.find((c) => fold(c.name) === fold(name)) ?? null

/**
 * Creates a category, or hands back the one that already goes by that name.
 * Typing a name is how they are made, so this runs whenever one is used.
 */
export async function addCategory(name) {
  const clean = String(name ?? '').trim()
  if (!clean) return null
  const existing = findCategory(clean)
  if (existing) return existing
  // The state can be behind the table — a migration makes rows too — and the
  // name is unique, so ask before inserting a second one.
  const [stored] = await query('SELECT * FROM categories WHERE lower(name) = lower(?)', [clean])
  if (stored) {
    await loadCategories()
    return stored
  }
  const now = new Date().toISOString()
  const row = { id: await freshId('ct', 'categories', clean), name: clean, created_at: now, updated_at: now }
  await insert('categories', row)
  await loadCategories()
  return row
}

/**
 * The id to write for a category name: `null` for none, and the row made if
 * the name is new. This is what every write path stores.
 */
export async function categoryIdFor(name) {
  return (await addCategory(name))?.id ?? null
}

/**
 * Renames it everywhere at once — which is one row, since everything filed
 * under it points at the row rather than repeating the name. The ledger and
 * the plans are reloaded only because they carry the name joined in.
 */
export async function renameCategory(id, name) {
  const clean = String(name ?? '').trim()
  const category = state.categories.find((c) => c.id === id)
  if (!clean || !category) return
  const clash = findCategory(clean)
  if (clash && clash.id !== id) return
  const stamp = new Date().toISOString()
  await run('UPDATE categories SET name = ?, updated_at = ? WHERE id = ?', [clean, stamp, id])
  await Promise.all([loadCategories(), loadTransactions(), loadPlans()])
}

/**
 * Deletes the category and takes it off everything filed under it. The rows
 * themselves stay — losing a label is not losing a transaction — and become
 * uncategorised, which the app already has an answer for.
 */
export async function deleteCategory(id) {
  const category = state.categories.find((c) => c.id === id)
  if (!category) return
  const stamp = new Date().toISOString()
  await run('BEGIN')
  try {
    // ON DELETE SET NULL would do this, but only where foreign keys are
    // enforced, and not every driver turns them on.
    await run('UPDATE transactions SET category_id = NULL, updated_at = ? WHERE category_id = ?', [
      stamp,
      id,
    ])
    await run('UPDATE plans SET category_id = NULL, updated_at = ? WHERE category_id = ?', [stamp, id])
    await run('DELETE FROM categories WHERE id = ?', [id])
    await run('COMMIT')
  } catch (err) {
    await run('ROLLBACK')
    throw err
  }
  await Promise.all([loadCategories(), loadTransactions(), loadPlans()])
}

/**
 * Makes sure a row exists for each of these names, in one pass. For the bulk
 * paths — sample data, a statement import — that write transactions directly
 * and would otherwise leave their categories without anything to hang a tag on.
 */
export async function ensureCategories(names) {
  const wanted = [...new Set((names ?? []).map((n) => String(n ?? '').trim()).filter(Boolean))]
  const missing = wanted.filter((name) => !findCategory(name))
  if (!missing.length) return 0
  const now = new Date().toISOString()
  for (const name of missing) {
    await insert('categories', { id: await freshId('ct', 'categories', name), name, created_at: now, updated_at: now })
  }
  await loadCategories()
  return missing.length
}

/** Replaces a category's tags with exactly this set. */
export async function setCategoryTags(categoryId, tagIds) {
  if (!categoryId) return
  await run('DELETE FROM category_tags WHERE category_id = ?', [categoryId])
  for (const tagId of new Set(tagIds ?? [])) {
    await run('INSERT OR IGNORE INTO category_tags (category_id, tag_id) VALUES (?, ?)', [
      categoryId,
      tagId,
    ])
  }
  await loadCategories()
}

/** The tag ids hung on one category. */
export const categoryTagIds = (categoryId) => state.categoryTags[categoryId] ?? []

/** The tag ids a transaction inherits from the category it is filed under. */
export const inheritedTagIds = (category) => {
  const row = category ? findCategory(category) : null
  return row ? categoryTagIds(row.id) : []
}

/**
 * The id a new category or tag gets: made from its name (`nameId`), so devices
 * syncing agree on it — unless a renamed row still wears it, when any id will do.
 */
export async function freshId(prefix, table, name) {
  const id = nameId(prefix, name)
  const [taken] = await query(`SELECT 1 AS x FROM ${table} WHERE id = ?`, [id])
  return taken ? uid(prefix) : id
}
